import { Box } from '@mui/material'
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog'
import { t } from 'i18next'
import { ComponentType, useCallback, useState } from 'react'
import RawCropper from 'react-easy-crop'
import Logger from 'services/Logger'
import { APP_COLOR } from 'utils/colors'
import { compressBase64 } from 'utils/file'

interface Point {
  x: number
  y: number
}

interface Area {
  width: number
  height: number
  x: number
  y: number
}

/**
 * `react-easy-crop`'s default export is a class component whose typed
 * `render()` return value clashes with React 19's stricter `ReactNode`.
 * The runtime component is valid, so we cast it to a component typed with the
 * props we actually use.
 */
const Cropper = RawCropper as unknown as ComponentType<{
  image: string
  crop: Point
  zoom: number
  aspect: number
  onCropChange: (location: Point) => void
  onZoomChange: (zoom: number) => void
  onCropComplete: (croppedArea: Area, croppedAreaPixels: Area) => void
}>

/**
 * Aspect ratio shown in the crop preview. This is the "safe" area the user
 * positions and the region web displays (via objectFit: cover on a 2:1 head).
 */
const COVER_ASPECT_RATIO = 2 / 1

/**
 * Aspect ratio of the actually exported image. It is TALLER than the preview so
 * mobile apps (Android/iOS) that centerCrop into a taller header have extra
 * image above/below the 2:1 selection to fill with, avoiding grey bars.
 *
 * Web keeps rendering the centered 2:1 band, which equals the user's selection,
 * so the on-screen preview and the web result stay identical.
 *
 * Lower this value to add even more vertical margin (e.g. 5 / 4 or 6 / 5) if
 * some devices still show bars; raise it toward 2 / 1 for a smaller payload.
 */
const EXPORT_ASPECT_RATIO = 4 / 3

interface ImageCropDialogProps {
  open: boolean
  imageSrc: string
  onClose: () => void
  onCropSave: (croppedImageBase64: string) => void
}

/**
 * Given an image source and the 2:1 preview crop area, returns a base64 string
 * of the exported image (without the 'data:image/jpeg;base64,' prefix).
 *
 * The exported region is expanded vertically (centered on the 2:1 selection) to
 * EXPORT_ASPECT_RATIO, clamped to the source image bounds, so mobile headers
 * that are taller than 2:1 fill without grey bars.
 */
const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: Area
): Promise<string> => {
  const image = new Image()
  image.src = imageSrc
  await new Promise((resolve, reject) => {
    image.onload = resolve
    image.onerror = reject
  })

  // Keep the same horizontal extent as the 2:1 preview selection.
  const exportWidth = pixelCrop.width

  // Taller export: derive height from the wider vertical ratio, but never
  // request more than the source image can provide.
  const exportHeight = Math.min(
    Math.round(exportWidth / EXPORT_ASPECT_RATIO),
    image.naturalHeight
  )

  // Center the taller region on the middle of the user's 2:1 selection, then
  // clamp it inside the image so we never sample outside its bounds.
  const cropCenterY = pixelCrop.y + pixelCrop.height / 2
  const sourceY = Math.max(
    0,
    Math.min(
      Math.round(cropCenterY - exportHeight / 2),
      image.naturalHeight - exportHeight
    )
  )

  const canvas = document.createElement('canvas')
  canvas.width = exportWidth
  canvas.height = exportHeight
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    throw new Error('Canvas 2D context not available')
  }

  ctx.drawImage(
    image,
    pixelCrop.x,
    sourceY,
    exportWidth,
    exportHeight,
    0,
    0,
    exportWidth,
    exportHeight
  )

  return new Promise((resolve) => {
    const base64withPrefix = canvas.toDataURL('image/jpeg', 0.9)
    resolve(base64withPrefix)
  })
}

export default function ImageCropDialog({
  open,
  imageSrc,
  onClose,
  onCropSave
}: ImageCropDialogProps) {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)

  const onCropComplete = useCallback(
    (_croppedArea: Area, croppedAreaPixelsTarget: Area) => {
      setCroppedAreaPixels(croppedAreaPixelsTarget)
    },
    []
  )

  const handleSave = async () => {
    if (!croppedAreaPixels) return

    try {
      setIsProcessing(true)
      const croppedDataUrl = await getCroppedImg(imageSrc, croppedAreaPixels)
      const compressedBase64 = await compressBase64(croppedDataUrl)
      // compressBase64 returns a plain base64 string (no data: prefix)
      const base64Data =
        typeof compressedBase64 === 'string' && compressedBase64.includes(',')
          ? compressedBase64.split(',')[1]
          : (compressedBase64 as string)
      onCropSave(base64Data)
    } catch (e) {
      Logger.red('ImageCropDialog', 'Failed to crop image', e)
    } finally {
      setIsProcessing(false)
      onClose()
    }
  }

  return (
    <ConfirmationDialog
      open={open}
      title={t('cropImage', 'Adjust Cover Image')}
      leftButtonColor={'black'}
      leftButton={`${t('cancel')}`}
      rightButtonColor={APP_COLOR}
      rightButton={isProcessing ? t('saving', 'Saving...') : t('save')}
      rightButtonDisabled={isProcessing}
      handleClose={onClose}
      handleLeftButton={onClose}
      handleRightButton={handleSave}
      fullWidth
      content={
        <Box
          sx={{
            position: 'relative',
            height: 400,
            width: '100%',
            minWidth: { xs: 'auto', sm: 400 }
          }}
        >
          {imageSrc && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: '#333'
              }}
            >
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={COVER_ASPECT_RATIO}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </Box>
          )}
        </Box>
      }
    />
  )
}
